import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import QuoteLinkHeader from 'components/pageLayout/QuoteLinkHeader';
import QuoteLinkLayout from 'components/pageLayout/QuoteLinkLayout';
import LoadingIcon from 'components/pageLayout/loading';
import QuoteNotFound from 'pages/QuoteNotFound';
import { setTheme } from 'reducers/ui/uiSlice';
import { fetchTemplateCode } from 'services/QuoteLinkService';
import { CONTACT_TYPE, QUOTE_SECTION } from 'shared/constants/common';
import ContactAgent from './ContactAgent';
import DownloadItinerary from './DownloadItinerary';
import HolidaySummary from './HolidaySummary';
import Message from './Message';
import MessageQuoteConsultant from './MessageQuoteConsultant';
import NavigationBar from './NavigationBar';
import Overview from './Overview';
import PhoneDetails from './PhoneDetails';
import Price from './Price';
import ShareSocialMedia from './ShareSocialMedia';

export default function QuoteLinkPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [overviewData, setOverviewData] = useState({});
  const [templateCode, setTemplateCode] = useState('');
  const [contactType, setContactType] = useState(CONTACT_TYPE.BOOK);
  const [showPhoneDetails, setShowPhoneDetails] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isIntroInView, setIsIntroInView] = useState(false);
  const [isOverviewInView, setIsOverviewInView] = useState(false);
  const [isSummaryInView, setIsSummaryInView] = useState(false);
  const [isPriceInView, setIsPriceInView] = useState(false);

  const { quoteId } = useParams();

  useEffect(() => {
    let section = QUOTE_SECTION.INTRODUCTION;
    if (isIntroInView) {
      section = QUOTE_SECTION.INTRODUCTION;
    } else if (isOverviewInView) {
      section = QUOTE_SECTION.OVERVIEW;
    } else if (isSummaryInView) {
      section = QUOTE_SECTION.SUMMARY;
    } else if (isPriceInView) {
      section = QUOTE_SECTION.PRICE;
    } else {
      section = QUOTE_SECTION.PRICE;
    }
    setActiveSection(section);
  }, [isIntroInView, isOverviewInView, isSummaryInView, isPriceInView]);

  useEffect(() => {
    const getTemplateCode = async (id, templateName) => {
      const { data } = await fetchTemplateCode(id, templateName);
      dispatch(setTheme(JSON.parse(data.brandTheme.replace(/'/g, '"'))));
      if (data) {
        data.itinerary = JSON.parse(data.itinerary.replace(/'/g, '"'));
        data.itineraryDays = data.itineraryDays
          .trim()
          .slice(0, -1)
          .replace(/\s/g, '')
          .split(',');
      }
      setOverviewData(JSON.parse(data.overview.replace(/'/g, '"')));
      setTemplateCode(data);
      setLoading(false);
    };

    if (quoteId) {
      getTemplateCode(quoteId, [
        'overview',
        'quoteTitle',
        'preparedBy',
        'brandPhoneNumber',
        'openingTimes',
        'websiteUrl',
        'quoteDestination',
        'brandLogo',
        'itinerary',
        'itineraryDays',
        'brandOpeningHours',
        'brandTheme',
        'price',
        'footerInfo',
        'reference',
        'visitorLongId',
        'authorAvatar',
        'authorAboutMe',
        'welcomeText',
        'quoteAuthor',
        'brandName',
      ]);
    }
  }, [quoteId, dispatch]);

  if (loading) {
    return <LoadingIcon />;
  }

  if (!templateCode.reference && !loading) {
    return <QuoteNotFound />;
  }

  return (
    <QuoteLinkLayout
      brandLogo={templateCode.brandLogo}
      footerInfo={
        templateCode && JSON.parse(templateCode.footerInfo.replace(/'/g, '"'))
      }
    >
      {showPhoneDetails && (
        <PhoneDetails
          phoneNumber={templateCode.brandPhoneNumber}
          openingHours={templateCode.brandOpeningHours}
          closePhoneDetails={() => setShowPhoneDetails(false)}
        />
      )}
      <QuoteLinkHeader
        quoteTitle={templateCode.quoteTitle}
        preparedBy={templateCode.preparedBy}
        brandPhoneNumber={templateCode.brandPhoneNumber}
        openingTimes={templateCode.openingTimes}
        websiteUrl={templateCode.websiteUrl}
        quoteDestination={templateCode.quoteDestination}
        brandLogo={templateCode.brandLogo}
        brandTheme={templateCode.brandTheme}
        authorAvatar={templateCode.authorAvatar}
        openPhoneDetails={() => setShowPhoneDetails(true)}
      />
      <div className="w-full">
        <NavigationBar activeLink={activeSection} />
        <div className="w-full flex justify-center bg-[#FAFAFA]">
          <div className="w-full md:w-[95%] lg:max-w-[95%] grid:xl:max-w-[1440px]">
            <div className="flex flex-wrap justify-between content-spacer">
              <div className="lg:w-[65%] xl:mr-[40px] mb-[70px]">
                <Message
                  className="max-w-[1000px] 3xl:mt-7 scroll-mt-20 xs:scroll-mt-16"
                  updateInView={setIsIntroInView}
                  aboutMeText={templateCode.authorAboutMe}
                />
                <Message
                  className="max-w-[1000px] 3xl:mt-7 scroll-mt-20 xs:scroll-mt-16"
                  quoteId={quoteId}
                  updateInView={setIsIntroInView}
                  welcomeText={templateCode.welcomeText}
                />
                <DownloadItinerary reference={templateCode.reference} />
                <Overview
                  className="mt-9 scroll-mt-20 xs:scroll-mt-16"
                  updateInView={setIsOverviewInView}
                  overviewData={overviewData}
                />
                <HolidaySummary
                  className=" relative mt-9 scroll-mt-20 xs:scroll-mt-16"
                  itinerary={templateCode.itinerary}
                  itineraryDays={templateCode.itineraryDays}
                  updateInView={setIsSummaryInView}
                />
                <Price
                  className="scroll-mt-20 xs:scroll-mt-16"
                  updateInView={setIsPriceInView}
                  price={templateCode.price}
                />
                <ContactAgent
                  visitorLongId={templateCode.visitorLongId}
                  contactType={contactType}
                  updateContactType={setContactType}
                  className="mt-9"
                />
              </div>
              <div className="flex flex-col md:flex-row lg:flex-col md:justify-around lg:justify-start items-center w-full mb-10 xs:mt-[3rem] lg:mt-0 lg:w-[18rem] md:mb-0 md:h-[215px]">
                <ShareSocialMedia brandName={templateCode.brandName} />
                <MessageQuoteConsultant
                  quoteAuthor={templateCode.quoteAuthor}
                  agentAvatar={templateCode.agentAvatar}
                  messageAgentClicked={() =>
                    setContactType(CONTACT_TYPE.MORE_INFO)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </QuoteLinkLayout>
  );
}
